import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import { getReturns, createReturn, getSaleItems } from '../services/api';
import { debounce } from 'lodash';
import { showSuccess, showError, showWarning, showConfirm } from '../services/swal';

const Returns = () => {
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({ total_volume: 0, count: 0, today_returns: 0 });
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // New Return Modal State
    const [showModal, setShowModal] = useState(false);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [saleData, setSaleData] = useState(null);
    const [returnItems, setReturnItems] = useState([]);
    const [returnReason, setReturnReason] = useState('Damaged');
    const [customReason, setCustomReason] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Detail Modal State
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedReturn, setSelectedReturn] = useState(null);

    const fetchReturns = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                search: searchTerm,
                start_date: startDate,
                end_date: endDate
            };
            const res = await getReturns(params);
            setReturns(res.data.data || []);
            setCurrentPage(res.data.current_page);
            setLastPage(res.data.last_page);
            
            // Calculate simple stats from current view for dashboard
            if (page === 1) {
                const total = res.data.data.reduce((acc, curr) => acc + parseFloat(curr.total_refund_amount), 0);
                setStats({
                    total_volume: total,
                    count: res.data.total,
                    today_returns: res.data.data.filter(r => new Date(r.created_at).toDateString() === new Date().toDateString()).length
                });
            }
        } catch (err) {
            showError('Error', 'Failed to fetch returns history.');
        } finally {
            setLoading(false);
        }
    }, [searchTerm, startDate, endDate]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetch = useCallback(debounce((page) => fetchReturns(page), 500), [fetchReturns]);

    useEffect(() => {
        debouncedFetch(1);
        return () => debouncedFetch.cancel();
    }, [searchTerm, startDate, endDate, debouncedFetch]);

    const handlePageChange = (page) => {
        fetchReturns(page);
    };

    const handleSearchInvoice = async () => {
        if (!invoiceNumber.trim()) return;
        setIsSearching(true);
        try {
            const res = await getSaleItems(invoiceNumber);
            const data = res.data.sale || res.data;
            const items = res.data.items || res.data;
            
            setSaleData(data);
            setReturnItems(items.map(item => ({
                ...item,
                return_qty: 0,
                max_qty: item.quantity
            })));
        } catch (err) {
            showError('Not Found', 'Could not find any sale with this invoice number. Please check the ID or Invoice #.');
        } finally {
            setIsSearching(false);
        }
    };

    const handleQtyChange = (productId, qty) => {
        const numericQty = parseInt(qty) || 0;
        setReturnItems(prev => prev.map(item => {
            if (item.product_id === productId) {
                const safeQty = Math.max(0, Math.min(numericQty, item.max_qty));
                return { ...item, return_qty: safeQty };
            }
            return item;
        }));
    };

    const calculateRefundTotal = () => {
        return returnItems.reduce((acc, item) => {
            const price = parseFloat(item.unit_price) - (parseFloat(item.discount) || 0);
            return acc + (price * item.return_qty);
        }, 0);
    };

    const handleSubmitReturn = async () => {
        const itemsToReturn = returnItems.filter(item => item.return_qty > 0);
        if (itemsToReturn.length === 0) {
            showWarning('Selection Required', 'Please specify at least one item and quantity to return.');
            return;
        }

        const finalReason = returnReason === 'Other' ? customReason : returnReason;
        if (returnReason === 'Other' && !customReason.trim()) {
            showWarning('Reason Required', 'Please provide a reason for the return.');
            return;
        }

        const confirm = await showConfirm('Confirm Return', `Are you sure you want to process this refund for Rs. ${calculateRefundTotal().toLocaleString()}? Stock will be restored.`);
        if (!confirm.isConfirmed) return;

        setLoading(true);
        try {
            await createReturn({
                sale_id: saleData.id,
                reason: finalReason,
                items: itemsToReturn.map(item => ({
                    product_id: item.product_id,
                    quantity: item.return_qty
                }))
            });
            showSuccess('Success', 'Return processed and inventory updated.');
            resetModal();
            fetchReturns(1);
        } catch (err) {
            showError('Failed', err.response?.data?.message || 'Error processing return.');
        } finally {
            setLoading(false);
        }
    };

    const resetModal = () => {
        setShowModal(false);
        setInvoiceNumber('');
        setSaleData(null);
        setReturnItems([]);
        setReturnReason('Damaged');
        setCustomReason('');
    };

    const openDetail = (ret) => {
        setSelectedReturn(ret);
        setShowDetailModal(true);
    };

    return (
        <Layout title="Product Returns Audit">
            {/* Analytical Snapshot */}
            <div className="row g-3 mb-4">
                <div className="col-12 col-md-4">
                    <div className="report-stat-card border-0 shadow-sm animate__animated animate__fadeIn">
                        <div className="d-flex align-items-center gap-3">
                            <div className="icon-badge bg-danger bg-opacity-10 text-danger" style={{ width: '56px', height: '56px' }}>
                                <i className="bi bi-arrow-counterclockwise fs-4"></i>
                            </div>
                            <div>
                                <small className="text-muted d-block fw-bold mb-0">Refund Volume</small>
                                <h4 className="fw-bold mb-0">Rs. {stats.total_volume.toLocaleString()}</h4>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-4">
                    <div className="report-stat-card border-0 shadow-sm animate__animated animate__fadeIn" style={{ animationDelay: '0.1s' }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="icon-badge bg-primary bg-opacity-10 text-primary" style={{ width: '56px', height: '56px' }}>
                                <i className="bi bi-file-earmark-text fs-4"></i>
                            </div>
                            <div>
                                <small className="text-muted d-block fw-bold mb-0">Audit Logs</small>
                                <h4 className="fw-bold mb-0">{stats.count} <span className="small text-muted fw-normal">TX</span></h4>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-4">
                    <div className="report-stat-card border-0 shadow-sm animate__animated animate__fadeIn" style={{ animationDelay: '0.2s' }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="icon-badge bg-warning bg-opacity-10 text-warning" style={{ width: '56px', height: '56px' }}>
                                <i className="bi bi-calendar-check fs-4"></i>
                            </div>
                            <div>
                                <small className="text-muted d-block fw-bold mb-0">Today's Logs</small>
                                <h4 className="fw-bold mb-0">{stats.today_returns} <span className="small text-muted fw-normal">Items</span></h4>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Discovery Bar */}
            <div className="report-discovery-bar shadow-sm border-0 mb-4 animate__animated animate__fadeInUp">
                <div className="row g-3 align-items-center">
                    <div className="col-12 col-xl-5">
                        <div className="search-wrapper position-relative">
                            <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                            <input
                                type="text"
                                className="form-control pos-search-input border-2"
                                placeholder="Audit by Return #, Invoice # or Customer..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ height: '52px', borderRadius: '14px', paddingLeft: '3.2rem' }}
                            />
                        </div>
                    </div>
                    <div className="col-6 col-md-3 col-xl-2">
                        <input 
                            type="date" 
                            className="form-control border-2" 
                            value={startDate} 
                            onChange={e => setStartDate(e.target.value)}
                            style={{ height: '52px', borderRadius: '14px' }}
                        />
                    </div>
                    <div className="col-6 col-md-3 col-xl-2">
                        <input 
                            type="date" 
                            className="form-control border-2" 
                            value={endDate} 
                            onChange={e => setEndDate(e.target.value)}
                            style={{ height: '52px', borderRadius: '14px' }}
                        />
                    </div>
                    <div className="col-12 col-md-6 col-xl-3 d-flex gap-2">
                        <button className="btn btn-light border-2 fw-bold flex-grow-1" onClick={() => { setSearchTerm(''); setStartDate(''); setEndDate(''); }} style={{ height: '52px', borderRadius: '14px' }}>
                            <i className="bi bi-arrow-counterclockwise"></i>
                        </button>
                        <button className="btn btn-primary fw-bold flex-grow-1 d-flex align-items-center justify-content-center gap-2" onClick={() => setShowModal(true)} style={{ height: '52px', borderRadius: '14px' }}>
                            <i className="bi bi-plus-lg fs-5"></i> <span className="d-none d-sm-inline">New Return</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Audit Grid - Desktop View */}
            <div className="report-table shadow-sm border-0 d-none d-md-block animate__animated animate__fadeIn">
                <div className="table-responsive">
                    <table className="table align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-4">Audit Trace</th>
                                <th>Source Invoice</th>
                                <th>Client / Entity</th>
                                <th className="text-end">Refund Value</th>
                                <th>Audit Date</th>
                                <th className="text-end pe-4">Management</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && returns.length === 0 ? (
                                <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border text-primary"></div></td></tr>
                            ) : returns.length === 0 ? (
                                <tr><td colSpan="6" className="text-center py-5 text-muted">No return logs found matching your filters.</td></tr>
                            ) : returns.map(ret => (
                                <tr key={ret.id} className="hover-light transition-all">
                                    <td className="ps-4 py-3">
                                        <div className="fw-bold text-primary">{ret.return_number}</div>
                                        <span className="badge bg-danger bg-opacity-10 text-danger border-0 small">REVERSED</span>
                                    </td>
                                    <td>
                                        <div className="fw-medium text-dark">{ret.sale?.invoice_number || 'N/A'}</div>
                                        <small className="text-muted">Direct Audit</small>
                                    </td>
                                    <td>
                                        <div className="fw-bold text-dark">{ret.customer?.name || 'Walk-in'}</div>
                                        <small className="text-muted">{ret.customer?.phone || 'Guest'}</small>
                                    </td>
                                    <td className="text-end fw-bold text-danger">Rs. {parseFloat(ret.total_refund_amount).toLocaleString()}</td>
                                    <td>
                                        <div className="fw-medium text-dark">{new Date(ret.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}</div>
                                        <small className="text-muted">{new Date(ret.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                                    </td>
                                    <td className="text-end pe-4">
                                        <button className="btn btn-sm btn-light border rounded-pill px-3 hover-shadow" onClick={() => openDetail(ret)}>
                                            <i className="bi bi-info-circle me-1"></i> Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Audit Grid - Mobile View */}
            <div className="d-md-none animate__animated animate__fadeIn">
                {loading && returns.length === 0 ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                ) : returns.length === 0 ? (
                    <div className="text-center py-5 text-muted bg-white rounded-4 shadow-sm border">No return logs found.</div>
                ) : (
                    <div className="row g-3">
                        {returns.map(ret => (
                            <div key={ret.id} className="col-12" onClick={() => openDetail(ret)}>
                                <div className="card border-0 shadow-sm rounded-4 p-3 hover-bg transition-all border-start border-4 border-danger">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <div className="fw-bold text-primary mb-0">{ret.return_number}</div>
                                            <small className="text-muted">Inv: {ret.sale?.invoice_number || 'N/A'}</small>
                                        </div>
                                        <div className="text-end">
                                            <div className="fw-bold text-danger fs-5">Rs. {parseFloat(ret.total_refund_amount).toLocaleString()}</div>
                                            <small className="text-muted">{new Date(ret.created_at).toLocaleDateString()}</small>
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="small fw-medium text-dark"><i className="bi bi-person me-1"></i> {ret.customer?.name || 'Walk-in'}</div>
                                        <span className="badge bg-danger bg-opacity-10 text-danger border-0">REVERSED</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {!loading && returns.length > 0 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-4 border-top bg-light-subtle no-print mt-3 rounded-4 shadow-sm">
                    <span className="text-muted small fw-medium mb-3 mb-md-0">
                        Showing <span className="text-dark">{(currentPage-1)*10 + 1}</span> to <span className="text-dark">{Math.min(currentPage*10, (currentPage-1)*10 + returns.length)}</span> of <span className="text-dark">{stats.count}</span>
                    </span>
                    <Pagination currentPage={currentPage} lastPage={lastPage} onPageChange={handlePageChange} />
                </div>
            )}

            {/* New Return Modal - The Processing Terminal */}
            {showModal && (
                <div className="modal d-block animate__animated animate__fadeIn" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }} onClick={(e) => e.target === e.currentTarget && resetModal()}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-fullscreen-sm-down">
                        <div className="modal-content border-0 shadow-lg overflow-hidden" style={{ borderRadius: '28px' }}>
                            {/* Premium Header */}
                            <div className="modal-header border-0 p-4 bg-primary text-white position-relative overflow-hidden">
                                <div className="position-absolute top-0 end-0 p-5 opacity-10" style={{ transform: 'translate(20%, -20%)' }}>
                                    <i className="bi bi-arrow-counterclockwise" style={{ fontSize: '10rem' }}></i>
                                </div>
                                <div className="position-relative z-1">
                                    <h5 className="modal-title fw-bold fs-4 d-flex align-items-center gap-2">
                                        <i className="bi bi-shield-lock-fill"></i> Return Terminal
                                    </h5>
                                    <p className="mb-0 opacity-75 small">Secure stock reversal and refund authorization</p>
                                </div>
                                <button type="button" className="btn-close btn-close-white position-relative z-1" onClick={resetModal}></button>
                            </div>

                            <div className="modal-body p-0">
                                {!saleData ? (
                                    <div className="p-5 text-center animate__animated animate__fadeInUp">
                                        <div className="icon-badge bg-primary bg-opacity-10 text-primary mx-auto mb-4 shadow-sm" style={{ width: '90px', height: '90px', fontSize: '3rem' }}>
                                            <i className="bi bi-qr-code-scan"></i>
                                        </div>
                                        <h3 className="fw-bold text-dark mb-2">Locate Source Invoice</h3>
                                        <p className="text-muted mb-5 mx-auto px-md-5">Audit the original sale to authorize itemized returns and restore warehouse stock.</p>
                                        
                                        <div className="mx-auto shadow-lg p-2 bg-white rounded-5 border-2 border border-primary border-opacity-25 d-flex align-items-center" style={{ maxWidth: '500px' }}>
                                            <input 
                                                type="text" 
                                                className="form-control form-control-lg border-0 ps-4 shadow-none" 
                                                placeholder="Enter Invoice # (e.g. INV-123)" 
                                                value={invoiceNumber}
                                                onChange={e => setInvoiceNumber(e.target.value)}
                                                onKeyPress={e => e.key === 'Enter' && handleSearchInvoice()}
                                                style={{ fontSize: '1.1rem', height: '64px', borderRadius: '32px' }}
                                            />
                                            <button className="btn btn-primary rounded-circle shadow-sm d-flex align-items-center justify-content-center ms-2" onClick={handleSearchInvoice} disabled={isSearching} style={{ width: '56px', height: '56px' }}>
                                                {isSearching ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-arrow-right fs-4"></i>}
                                            </button>
                                        </div>
                                        <div className="mt-4 d-flex justify-content-center gap-4 text-muted small">
                                            <span><i className="bi bi-check-circle-fill text-success me-1"></i>Stock Sync</span>
                                            <span><i className="bi bi-check-circle-fill text-success me-1"></i>Ledger Update</span>
                                            <span><i className="bi bi-check-circle-fill text-success me-1"></i>Audit Log</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="animate__animated animate__fadeIn">
                                        {/* Sale Header Info */}
                                        <div className="p-4 bg-light border-bottom d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="bg-white p-3 rounded-4 shadow-sm border">
                                                    <i className="bi bi-person-circle fs-3 text-primary"></i>
                                                </div>
                                                <div>
                                                    <h6 className="fw-bold mb-0 fs-5">{saleData.customer_name || 'Walk-in Customer'}</h6>
                                                    <small className="text-muted">Transaction ID: {saleData.invoice_number}</small>
                                                </div>
                                            </div>
                                            <div className="text-md-end bg-white px-4 py-2 rounded-pill border shadow-sm">
                                                <small className="text-muted d-block text-uppercase fw-bold ls-1" style={{ fontSize: '10px' }}>Total Sale Amount</small>
                                                <span className="fw-bold fs-5 text-primary">Rs. {parseFloat(saleData.final_amount).toLocaleString()}</span>
                                            </div>
                                        </div>

                                        {/* Items List - Card based for mobile, table for desktop */}
                                        <div className="p-4 overflow-auto custom-scrollbar" style={{ maxHeight: '350px' }}>
                                            <label className="form-label small fw-bold text-muted text-uppercase mb-3 px-1 ls-1">Select Items for Reversal</label>
                                            
                                            {/* Desktop Table */}
                                            <table className="table align-middle d-none d-md-table">
                                                <thead className="sticky-top bg-white">
                                                    <tr className="text-muted small">
                                                        <th className="border-0">Product Info</th>
                                                        <th className="text-center border-0">Sold</th>
                                                        <th className="text-center border-0" style={{ width: '160px' }}>Return Qty</th>
                                                        <th className="text-end border-0">Net Price</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {returnItems.map(item => (
                                                        <tr key={item.product_id}>
                                                            <td className="py-3">
                                                                <div className="fw-bold text-dark">{item.product_name}</div>
                                                                <small className="text-muted">SKU: {item.sku || 'N/A'}</small>
                                                            </td>
                                                            <td className="text-center fw-bold text-muted">{item.quantity}</td>
                                                            <td className="text-center">
                                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                                    <button className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center p-0" style={{ width: '32px', height: '32px', borderWidth: '2px' }} onClick={() => handleQtyChange(item.product_id, item.return_qty - 1)}>
                                                                        <i className="bi bi-dash-lg fw-bold"></i>
                                                                    </button>
                                                                    <input type="number" className="form-control text-center fw-bold border-0 bg-transparent p-0" style={{ maxWidth: '40px', fontSize: '1.1rem' }} value={item.return_qty} readOnly />
                                                                    <button className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center p-0" style={{ width: '32px', height: '32px', borderWidth: '2px' }} onClick={() => handleQtyChange(item.product_id, item.return_qty + 1)}>
                                                                        <i className="bi bi-plus-lg fw-bold"></i>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                            <td className="text-end fw-bold">Rs. {parseFloat(item.unit_price - (item.discount || 0)).toLocaleString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            {/* Mobile Cards */}
                                            <div className="d-md-none">
                                                {returnItems.map(item => (
                                                    <div key={item.product_id} className="card border-0 bg-light rounded-4 p-3 mb-3 border-start border-4 border-primary">
                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                            <div className="fw-bold text-dark pe-2">{item.product_name}</div>
                                                            <div className="fw-bold text-primary">Rs. {parseFloat(item.unit_price).toLocaleString()}</div>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <small className="text-muted fw-bold">Sold: {item.quantity}</small>
                                                            <div className="d-flex align-items-center gap-2 bg-white rounded-pill px-2 py-1 border shadow-sm">
                                                                <button className="btn btn-sm btn-outline-primary rounded-circle p-0 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px', borderWidth: '2px' }} onClick={() => handleQtyChange(item.product_id, item.return_qty - 1)}><i className="bi bi-dash-lg"></i></button>
                                                                <input type="number" className="form-control border-0 text-center fw-bold bg-transparent p-0" style={{ width: '30px', fontSize: '0.9rem' }} value={item.return_qty} readOnly />
                                                                <button className="btn btn-sm btn-outline-primary rounded-circle p-0 d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px', borderWidth: '2px' }} onClick={() => handleQtyChange(item.product_id, item.return_qty + 1)}><i className="bi bi-plus-lg"></i></button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Footer Controls */}
                                        <div className="p-4 bg-light border-top mt-auto">
                                            <div className="row g-3 align-items-center">
                                                <div className="col-12 col-md-7">
                                                    <div className="mb-3">
                                                        <label className="form-label small fw-bold text-muted text-uppercase ls-1">Return Reason</label>
                                                        <select className="form-select border-2 rounded-4 shadow-sm" style={{ height: '52px' }} value={returnReason} onChange={e => setReturnReason(e.target.value)}>
                                                            <option value="Damaged">Damaged / Defective</option>
                                                            <option value="Wrong Item">Wrong Item Shipped</option>
                                                            <option value="Customer Dissatisfied">Customer Dissatisfied</option>
                                                            <option value="Other">Other (Manual Audit)</option>
                                                        </select>
                                                    </div>
                                                    {returnReason === 'Other' && (
                                                        <div className="animate__animated animate__fadeIn">
                                                            <textarea className="form-control border-2 rounded-4 shadow-sm" rows="2" placeholder="Describe reversal reason..." value={customReason} onChange={e => setCustomReason(e.target.value)}></textarea>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="col-12 col-md-5 text-md-end">
                                                    <div className="bg-danger bg-opacity-10 p-3 rounded-4 border border-danger border-opacity-25 shadow-sm">
                                                        <small className="text-danger fw-bold text-uppercase d-block mb-1 ls-1" style={{ fontSize: '10px' }}>Credit Refund Amount</small>
                                                        <h2 className="fw-bold text-danger mb-0">Rs. {calculateRefundTotal().toLocaleString()}</h2>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row g-2 mt-4">
                                                <div className="col-6 col-md-3 order-2 order-md-1">
                                                    <button className="btn btn-light border-2 rounded-pill w-100 py-3 fw-bold text-muted" onClick={() => setSaleData(null)}>
                                                        <i className="bi bi-arrow-left me-2"></i>Back
                                                    </button>
                                                </div>
                                                <div className="col-12 col-md-9 order-1 order-md-2">
                                                    <button className="btn btn-primary rounded-pill w-100 py-3 fw-bold shadow-lg d-flex align-items-center justify-content-center gap-2" onClick={handleSubmitReturn} disabled={loading}>
                                                        {loading ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-check-circle-fill fs-5"></i> COMPLETE REVERSAL & REFUND</>}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Audit Detail Modal */}
            {showDetailModal && selectedReturn && (
                <div className="modal d-block animate__animated animate__fadeIn" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }} onClick={(e) => e.target === e.currentTarget && setShowDetailModal(false)}>
                    <div className="modal-dialog modal-dialog-centered modal-fullscreen-sm-down">
                        <div className="modal-content border-0 shadow-lg overflow-hidden" style={{ borderRadius: '28px' }}>
                            <div className="modal-header border-0 p-4 bg-danger text-white">
                                <h5 className="modal-title fw-bold fs-4">Audit Reversal Trace</h5>
                                <button className="btn-close btn-close-white" onClick={() => setShowDetailModal(false)}></button>
                            </div>
                            <div className="modal-body p-4 custom-scrollbar" style={{ maxHeight: '80vh' }}>
                                <div className="text-center mb-5">
                                    <div className="icon-badge bg-danger bg-opacity-10 text-danger mx-auto mb-3 shadow-sm" style={{ width: '80px', height: '80px', fontSize: '2.5rem' }}>
                                        <i className="bi bi-shield-check"></i>
                                    </div>
                                    <h4 className="fw-bold mb-1">{selectedReturn.return_number}</h4>
                                    <p className="text-muted small">Validated on {new Date(selectedReturn.created_at).toLocaleString()}</p>
                                </div>

                                <div className="row g-3 mb-5">
                                    <div className="col-6">
                                        <div className="p-3 bg-light rounded-4 border">
                                            <small className="text-muted d-block text-uppercase fw-bold mb-1 ls-1" style={{ fontSize: '10px' }}>Source Bill</small>
                                            <span className="fw-bold text-dark">{selectedReturn.sale?.invoice_number || 'N/A'}</span>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="p-3 bg-light rounded-4 border">
                                            <small className="text-muted d-block text-uppercase fw-bold mb-1 ls-1" style={{ fontSize: '10px' }}>Total Refund</small>
                                            <span className="fw-bold text-danger">Rs. {parseFloat(selectedReturn.total_refund_amount).toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>

                                <h6 className="fw-bold mb-3 px-1 text-uppercase ls-1 small text-muted">Audited Reversal Reason</h6>
                                <div className="p-3 bg-warning bg-opacity-10 rounded-4 border border-warning border-opacity-25 mb-5">
                                    <p className="mb-0 text-dark fw-medium">{selectedReturn.reason || 'No specific reason audited.'}</p>
                                </div>

                                <h6 className="fw-bold mb-3 px-1 text-uppercase ls-1 small text-muted">Stock Restoration Log</h6>
                                <div className="border rounded-4 overflow-hidden mb-5 shadow-sm">
                                    {selectedReturn.items?.map((item, idx) => (
                                        <div key={item.id} className={`d-flex justify-content-between align-items-center p-3 ${idx !== selectedReturn.items.length - 1 ? 'border-bottom' : ''}`}>
                                            <div>
                                                <div className="fw-bold text-dark">{item.product?.name}</div>
                                                <small className="text-muted">Unit Reversal: Rs. {parseFloat(item.refund_price).toLocaleString()}</small>
                                            </div>
                                            <div className="bg-danger bg-opacity-10 text-danger rounded-pill px-3 py-1 fw-bold small">+{item.quantity} Units</div>
                                        </div>
                                    ))}
                                </div>

                                <button className="btn btn-outline-dark w-100 rounded-pill py-3 fw-bold border-2" onClick={() => setShowDetailModal(false)}>
                                    CLOSE AUDIT TRACE
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <style>{`
                .report-stat-card {
                    background: var(--card-bg);
                    border-radius: 20px;
                    padding: 20px;
                    border: 1px solid var(--border-color);
                    transition: all 0.3s ease;
                }
                .report-discovery-bar {
                    background: var(--card-bg);
                    border-radius: 24px;
                    padding: 20px;
                    border: 1px solid var(--border-color);
                }
                .report-table {
                    background: var(--card-bg);
                    border-radius: 24px;
                    overflow: hidden;
                    border: 1px solid var(--border-color);
                }
                .icon-badge {
                    width: 45px;
                    height: 45px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.25rem;
                }
                
                /* Dark Mode Overrides */
                [data-bs-theme="dark"] .bg-white,
                [data-bs-theme="dark"] .bg-light,
                [data-bs-theme="dark"] .bg-light-subtle {
                    background-color: var(--card-bg) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .modal-content,
                [data-bs-theme="dark"] .modal-body .bg-white {
                    background-color: var(--card-bg) !important;
                }
                [data-bs-theme="dark"] .modal-body .bg-light,
                [data-bs-theme="dark"] .card-header.bg-light,
                [data-bs-theme="dark"] .modal-footer.bg-light {
                    background-color: rgba(255,255,255,0.02) !important;
                }
                [data-bs-theme="dark"] .pos-search-input,
                [data-bs-theme="dark"] .form-control,
                [data-bs-theme="dark"] .form-select {
                    background-color: rgba(255,255,255,0.03) !important;
                    border-color: var(--border-color) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .table {
                    --bs-table-bg: transparent;
                    --bs-table-color: var(--text-color);
                    --bs-table-border-color: var(--border-color);
                }
                [data-bs-theme="dark"] .hover-light:hover {
                    background-color: rgba(255,255,255,0.03) !important;
                }
                [data-bs-theme="dark"] .text-dark {
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .text-muted {
                    color: var(--text-muted-color) !important;
                }
            `}</style>
        </Layout>
    );
};

export default Returns;
